import React, { useState } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ActivityTimeline } from '../components/trip/ActivityTimeline';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { copyToClipboard } from '../utils/clipboard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MapPin, Calendar, Users, CreditCard, 
  TrendingUp, 
  Share2, Copy, CheckCircle2, Mail, MessageSquare, 
  UserPlus, Send, Plus, Trash2, Crown, Link2
} from 'lucide-react';
import { format, parseISO, differenceInDays, isBefore } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function TripOverview() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const context = useOutletContext();
  const { trip, members } = context || {};
  const { api } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en-US' ? enUS : ptBR;
  
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [membersListOpen, setMembersListOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteList, setInviteList] = useState([{ name: '', contact: '' }]);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');

  if (!trip) {
    return null;
  }

  const startDate = trip.start_date ? (typeof trip.start_date === 'string' ? parseISO(trip.start_date) : trip.start_date) : null;
  const endDate = trip.end_date ? (typeof trip.end_date === 'string' ? parseISO(trip.end_date) : trip.end_date) : null;
  const now = new Date();
  const daysUntil = startDate ? differenceInDays(startDate, now) : null;
  const totalDays = startDate && endDate ? differenceInDays(endDate, startDate) + 1 : null;
  const isUpcoming = startDate ? isBefore(now, startDate) : false;

  // Public URL for invite links (uses env var in production, window.location.origin in development)
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const inviteCode = trip.invite_code;
  const inviteLink = `${PUBLIC_URL}/dashboard?join=${inviteCode}`;

  const defaultMessage = language === 'en-US' 
    ? `Hello! 🌴\n\nYou are invited to join the trip "${trip.name}" to ${trip.destination}!\n\nClick the link below to join:\n${inviteLink}\n\nInvite code: ${inviteCode}\n\nLet's go! 🚀`
    : `Olá! 🌴\n\nVocê está convidado(a) para participar da viagem "${trip.name}" para ${trip.destination}!\n\nClique no link abaixo para entrar no grupo:\n${inviteLink}\n\nCódigo de convite: ${inviteCode}\n\nVamos juntos! 🚀`;

  const handleCopyLink = async () => {
    const success = await copyToClipboard(inviteLink);
    if (success) {
      setCopied(true);
      toast.success(t('common.copied'));
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCopied(false);
    } else {
      toast.error('Erro ao copiar link');
    }
  };

  const handleCopyCode = async () => {
    const success = await copyToClipboard(inviteCode);
    if (success) {
      toast.success(t('common.copied'));
    } else {
      toast.error('Erro ao copiar código');
    }
  };

  const handleAddInvite = () => {
    setInviteList([...inviteList, { name: '', contact: '' }]);
  };

  const handleRemoveInvite = (index) => {
    if (inviteList.length > 1) {
      setInviteList(inviteList.filter((_, i) => i !== index));
    }
  };

  const handleInviteChange = (index, field, value) => {
    const newList = [...inviteList];
    newList[index][field] = value;
    setInviteList(newList);
  };

  const handleSendInvites = async () => {
    const validInvites = inviteList.filter(i => i.name.trim() && i.contact.trim());
    
    if (validInvites.length === 0) {
      toast.error('Adicione pelo menos um convidado com nome e contato');
      return;
    }

    setSendingInvites(true);

    try {
      // Prepare contacts for bulk send
      const contacts = validInvites.map(invite => ({
        name: invite.name.trim(),
        contact: invite.contact.trim()
      }));

      // Send all invites via API (handles both email and WhatsApp)
      const response = await api.post(`/trips/${tripId}/send-bulk-invites`, {
        contacts,
        custom_message: inviteMessage || null
      });

      const { summary, results } = response.data;

      // Show success/error messages
      if (summary.sent > 0) {
        const messages = [];
        if (summary.whatsapp_sent > 0) {
          messages.push(`${summary.whatsapp_sent} WhatsApp`);
        }
        if (summary.email_sent > 0) {
          messages.push(`${summary.email_sent} email`);
        }
        toast.success(language === 'en-US' 
          ? `${summary.sent} invite(s) sent: ${messages.join(' + ')}`
          : `${summary.sent} convite(s) enviado(s): ${messages.join(' + ')}`
        );
      }

      if (summary.failed > 0) {
        const failedContacts = results
          .filter(r => !r.success)
          .map(r => r.name)
          .slice(0, 3)
          .join(', ');
        toast.error(language === 'en-US'
          ? `${summary.failed} invite(s) failed: ${failedContacts}${summary.failed > 3 ? '...' : ''}`
          : `${summary.failed} convite(s) falharam: ${failedContacts}${summary.failed > 3 ? '...' : ''}`
        );
      }

      // Clear the invite list after sending
      setInviteList([{ name: '', contact: '' }]);
      setInviteMessage('');

      // Close modal if all sent successfully
      if (summary.failed === 0) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setInviteModalOpen(false);
      }

    } catch (error) {
      console.error('Error sending invites:', error);
      toast.error(t('overview.errorSendingInvites'));
    } finally {
      setSendingInvites(false);
    }
  };

  const handleShareWhatsApp = () => {
    const message = inviteMessage || defaultMessage;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const stats = [];
  if (trip.estimated_budget) {
    stats.push({
      icon: TrendingUp,
      label: t('overview.budget'),
      value: `R$ ${trip.estimated_budget.toLocaleString('pt-BR')}`,
      color: 'text-green-500',
    });
  }

  return (
    <div className="space-y-6">
      {/* Trip Details Card with Members Count */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-slate-100 dark:border-slate-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t('overview.tripDetails')}</CardTitle>
              {/* Members Badge - Clickable - Shows participants list */}
              <button
                onClick={() => setMembersListOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 dark:bg-sky-900/30 hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors cursor-pointer"
                data-testid="members-badge-btn"
              >
                <Users className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                <span className="font-bold text-sky-700 dark:text-sky-300">{members?.length || 0}</span>
                <span className="text-sm text-sky-600 dark:text-sky-400">{t('trips.members').toLowerCase()}</span>
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Origin → Destination */}
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-slate-400" />
              <div>
                {trip.origin ? (
                  <p className="font-medium text-slate-900 dark:text-white">
                    {trip.origin} → {trip.destination}
                  </p>
                ) : (
                  <p className="font-medium text-slate-900 dark:text-white">{trip.destination}</p>
                )}
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {trip.origin ? t('overview.route') || 'Trajeto' : t('overview.destination')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-400" />
              <div>
                {startDate && endDate ? (
                  <>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {format(startDate, "dd 'de' MMMM", { locale: dateLocale })} - {format(endDate, "dd 'de' MMMM 'de' yyyy", { locale: dateLocale })}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {totalDays} {t('common.days')}
                      {daysUntil !== null && daysUntil > 0 && (
                        <span className="text-orange-500 font-medium ml-2">• {daysUntil} {t('dashboard.daysRemaining')}</span>
                      )}
                      {daysUntil !== null && daysUntil === 0 && (
                        <span className="text-green-500 font-medium ml-2">• {t('overview.startsToday')}</span>
                      )}
                      {daysUntil !== null && daysUntil < 0 && (
                        <span className="text-slate-400 ml-2">• {t('overview.tripInProgress')}</span>
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-purple-600 dark:text-purple-400">{t('overview.datesToDefine')}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('overview.waitingDestination')}</p>
                  </>
                )}
              </div>
            </div>

            {trip.estimated_budget && (
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    R$ {trip.estimated_budget.toLocaleString('pt-BR')}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('overview.estimatedBudget')}</p>
                </div>
              </div>
            )}

            {trip.description && (
              <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
                <p className="text-slate-600 dark:text-slate-400">{trip.description}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Activity Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <ActivityTimeline tripId={tripId} />
      </motion.div>

      {/* Participants Preview */}
      {members && members.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-slate-100 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{t('trips.members')}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{members.length}</Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-sky-600 hover:text-sky-700 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/30"
                    onClick={() => setInviteModalOpen(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    {t('trips.invite')}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex -space-x-2">
                {members.slice(0, 8).map((member) => (
                  <div
                    key={member.id}
                    className="w-10 h-10 rounded-full bg-orange-100 border-2 border-white dark:border-slate-800 flex items-center justify-center text-lg"
                    title={member.user_name}
                  >
                    {member.user_avatar || '😊'}
                  </div>
                ))}
                {members.length > 8 && (
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 border-2 border-white dark:border-slate-800 flex items-center justify-center text-sm font-medium text-slate-600 dark:text-slate-400">
                    +{members.length - 8}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Members List Modal */}
      <Dialog open={membersListOpen} onOpenChange={setMembersListOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-500" />
              Participantes da Viagem
            </DialogTitle>
            <DialogDescription>
              {members?.length || 0} {members?.length === 1 ? 'membro' : 'membros'} confirmado{members?.length !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-2 py-2">
              {members?.map((member) => (
                <div 
                  key={member.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-lg">
                    {member.user_avatar || '😊'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {member.user_name}
                      </p>
                      {member.role === 'organizer' && (
                        <Badge variant="outline" className="text-xs border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400">
                          <Crown className="w-3 h-3 mr-1" />
                          Organizador
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {member.role === 'organizer' ? 'Organizador' : 'Viajante'}
                    </p>
                  </div>
                </div>
              ))}

              {(!members || members.length === 0) && (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum participante ainda</p>
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => {
                setMembersListOpen(false);
                navigate(`/trip/${tripId}/members`);
              }}
            >
              <Users className="w-4 h-4 mr-2" />
              Ver detalhes
            </Button>
            <Button
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
              onClick={() => {
                setMembersListOpen(false);
                setInviteModalOpen(true);
              }}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Convidar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-500" />
              {t('overview.inviteMembers')}
            </DialogTitle>
            <DialogDescription>
              {t('overview.inviteDescription', { tripName: trip.name })}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="share" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-auto p-1 mb-4">
              <TabsTrigger 
                value="share" 
                className="flex flex-col items-center gap-1.5 py-3 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-600 dark:data-[state=active]:bg-orange-900/30 dark:data-[state=active]:text-orange-400"
              >
                <Link2 className="w-5 h-5" />
                <span className="text-xs font-medium">Compartilhar link</span>
              </TabsTrigger>
              <TabsTrigger 
                value="email" 
                className="flex flex-col items-center gap-1.5 py-3 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-600 dark:data-[state=active]:bg-orange-900/30 dark:data-[state=active]:text-orange-400"
              >
                <Mail className="w-5 h-5" />
                <span className="text-xs font-medium">Convidar via e-mail</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Share Link */}
            <TabsContent value="share" className="space-y-4 mt-0">
              {/* Link Input */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600 dark:text-slate-400">Link de convite</Label>
                <div className="flex gap-2">
                  <Input
                    value={inviteLink}
                    readOnly
                    className="flex-1 bg-slate-50 dark:bg-slate-800 text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                    className="shrink-0"
                  >
                    {copied ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Invite Code */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600 dark:text-slate-400">Código de convite</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <span className="flex-1 text-xl font-mono font-bold tracking-widest text-slate-900 dark:text-white text-center">
                    {inviteCode}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyCode}
                    className="shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Share buttons */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-400 dark:border-green-800"
                  onClick={handleShareWhatsApp}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Copiar link
                </Button>
              </div>

              <p className="text-xs text-center text-slate-500 dark:text-slate-400 pt-2">
                Compartilhe o link ou código com as pessoas que deseja convidar
              </p>
            </TabsContent>

            {/* Tab 2: Invite via Email */}
            <TabsContent value="email" className="space-y-4 mt-0">
              {/* Mass Invite Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-slate-600 dark:text-slate-400">Lista de convidados</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAddInvite}
                    className="text-orange-600 hover:text-orange-700 h-8"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                
                <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                  {inviteList.map((invite, index) => (
                    <div key={index} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-2 relative">
                      {inviteList.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveInvite(index)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Input
                        placeholder="Nome do convidado"
                        value={invite.name}
                        onChange={(e) => handleInviteChange(index, 'name', e.target.value)}
                        className="bg-white dark:bg-slate-800"
                      />
                      <Input
                        placeholder="E-mail ou telefone"
                        value={invite.contact}
                        onChange={(e) => handleInviteChange(index, 'contact', e.target.value)}
                        className="bg-white dark:bg-slate-800"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Message */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600 dark:text-slate-400">Mensagem personalizada (opcional)</Label>
                <Textarea
                  placeholder={defaultMessage}
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={5}
                  className="text-sm min-h-[120px]"
                />
              </div>

              {/* Send Button */}
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600"
                onClick={handleSendInvites}
                disabled={sendingInvites}
              >
                {sendingInvites ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar convites
                  </>
                )}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
